import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function queueKey(gameId, userId) {
  return `tmg_queue_${gameId}_${userId}`;
}

export function getSongQueue(gameId, userId) {
  try {
    const raw = localStorage.getItem(queueKey(gameId, userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearSongQueue(gameId, userId) {
  localStorage.removeItem(queueKey(gameId, userId));
}

function saveSongQueue(gameId, userId, song) {
  localStorage.setItem(queueKey(gameId, userId), JSON.stringify(song));
}

function parseWhitelistSong(str) {
  const dashIdx = str.indexOf(' - ');
  if (dashIdx !== -1) {
    return { title: str.slice(0, dashIdx).trim(), artist: str.slice(dashIdx + 3).trim() };
  }
  return { title: str.trim(), artist: '' };
}

// Renders the song queue form content — the toggle button is handled by the parent.
export default function SongQueuePanel({ game, userId, onQueueChange }) {
  const { t } = useTranslation();

  const rules = game.rules || {};
  const whitelistArtists = rules.whitelistArtists || [];
  const whitelistSongs = rules.whitelistSongs || [];
  const hasArtistWhitelist = rules.whitelistArtistsEnabled && whitelistArtists.length > 0;
  const hasSongWhitelist = rules.whitelistSongsEnabled && whitelistSongs.length > 0;

  const [queued, setQueued] = useState(() => getSongQueue(game.id, userId));
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [selectedWhitelistSong, setSelectedWhitelistSong] = useState('');

  function handleQueue(song) {
    saveSongQueue(game.id, userId, song);
    setQueued(song);
    onQueueChange?.(true);
    setTitle('');
    setArtist('');
    setSelectedWhitelistSong('');
  }

  function handleClearQueue() {
    clearSongQueue(game.id, userId);
    setQueued(null);
    onQueueChange?.(false);
  }

  function handleWhitelistSongSelect(value) {
    setSelectedWhitelistSong(value);
    if (value) {
      const parsed = parseWhitelistSong(value);
      setTitle(parsed.title);
      setArtist(parsed.artist);
    } else {
      setTitle('');
      setArtist('');
    }
  }

  function handleQueueSong() {
    if (!title.trim()) return;
    handleQueue({ title: title.trim(), artist: artist.trim() });
  }

  const canQueue = hasSongWhitelist ? !!selectedWhitelistSong : !!title.trim();

  return (
    <div id="song-queue-panel" data-testid="song-queue-panel" className="space-y-3">
      <p className="text-xs t-text-disabled">{t('songQueue.hint')}</p>

      {queued ? (
        <div className="t-bg-input t-border border rounded-xl p-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs t-text-disabled font-medium uppercase tracking-wider mb-0.5">
              {t('songQueue.queuedLabel')}
            </p>
            <p className="t-text text-sm font-medium truncate">{queued.title}</p>
            {queued.artist && (
              <p className="t-text-muted text-xs truncate">{queued.artist}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleClearQueue}
            className="t-text-error hover:opacity-80 text-xs t-focus-ring rounded px-2 py-1 shrink-0"
          >
            {t('songQueue.clear')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Song whitelist: single dropdown covering title + artist */}
          {hasSongWhitelist ? (
            <div>
              <label htmlFor="queue-song-whitelist" className="block text-sm t-text-muted mb-1">
                {t('songSelection.selectSongLabel')}
              </label>
              <select
                id="queue-song-whitelist"
                value={selectedWhitelistSong}
                onChange={(e) => handleWhitelistSongSelect(e.target.value)}
                className="w-full t-bg-input t-border-input border rounded-lg px-4 py-2 t-text t-focus-input text-sm"
              >
                <option value="">{t('songSelection.selectSongPlaceholder')}</option>
                {whitelistSongs.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="queue-title" className="block text-sm t-text-muted mb-1">
                  {t('songSelection.songTitle')}
                </label>
                <input
                  id="queue-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('songSelection.titlePlaceholder')}
                  maxLength={200}
                  className="w-full t-bg-input t-border-input border rounded-lg px-4 py-2 t-text t-placeholder t-focus-input text-sm"
                />
              </div>
              <div>
                <label htmlFor="queue-artist" className="block text-sm t-text-muted mb-1">
                  {t('songSelection.artist')}
                </label>
                {/* Artist whitelist: dropdown instead of free text */}
                {hasArtistWhitelist ? (
                  <select
                    id="queue-artist"
                    value={artist}
                    onChange={(e) => setArtist(e.target.value)}
                    className="w-full t-bg-input t-border-input border rounded-lg px-4 py-2 t-text t-focus-input text-sm"
                  >
                    <option value="">{t('songSelection.selectArtistPlaceholder')}</option>
                    {whitelistArtists.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="queue-artist"
                    type="text"
                    value={artist}
                    onChange={(e) => setArtist(e.target.value)}
                    placeholder={t('songSelection.artistPlaceholder')}
                    maxLength={80}
                    className="w-full t-bg-input t-border-input border rounded-lg px-4 py-2 t-text t-placeholder t-focus-input text-sm"
                  />
                )}
              </div>
            </>
          )}
          <button
            type="button"
            onClick={handleQueueSong}
            disabled={!canQueue}
            className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-2 text-sm transition-colors t-focus-ring"
          >
            {t('songQueue.queueSong')}
          </button>
        </div>
      )}
    </div>
  );
}
